import { Divider, Grid, Box, TextField } from '@material-ui/core';
import { useFormikContext } from 'formik';
import React, { useMemo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { FormSection, NumberTextField } from '~/modules/common/components';
import { ShowCommentsOnInvoiceCheckBox } from '~/modules/billing-invoicing/common/components';
import { useIsBreakpointDown, useHasFeatureFlag } from '~/modules/common/hooks';
import { getError, hasError } from '~/util';
import useStyles from './useStyles';
import { BillLineItemsByDropdown } from './BillLineItemsByDropdown';

export const highlightTarget = event => event.target.select();

const BillingCardEditable = ({
  billingCardResourceKeys,
  isSubmitting,
  errors
}) => {
  const isMobile = useIsBreakpointDown('sm');
  const isPsaPrpCommentsOnInvoiceEnabled = useHasFeatureFlag({
    featureFlag: 'isPsaPrpCommentsOnInvoiceEnabled'
  });
  const label = resourceKey => <FormattedMessage id={resourceKey} />;
  const classes = useStyles();

  const paymentTermAdornment = useMemo(
    () => (
      <span className={classes.net}>
        {label(billingCardResourceKeys.paymentTermNet)}
      </span>
    ),
    [billingCardResourceKeys.paymentTermNet, classes.net]
  );
  const inputRefCallback = useCallback(
    errorId => inputRef => {
      if (isSubmitting && hasError(errors, errorId) && inputRef)
        inputRef.focus();
    },
    [isSubmitting, errors]
  );

  const { values, setFieldValue } = useFormikContext();

  const {
    defaultInvoicePaymentTerm,
    billLineItemsBy,
    description,
    internalNotes,
    showComments
  } = values;

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12}>
          <FormSection
            classes={useMemo(
              () => ({
                legend: classes.legend
              }),
              [classes.legend]
            )}
            title={label(billingCardResourceKeys.schedule)}
          />
        </Grid>
        <Grid item xs={12} sm={4} data-qe-id="PaymentTermDropdown">
          <NumberTextField
            id="paymentTerm"
            variant="filled"
            fullWidth
            label={label(billingCardResourceKeys.paymentTerm)}
            startAdornment={paymentTermAdornment}
            min={0}
            max={25000}
            step={1}
            value={defaultInvoicePaymentTerm}
          />
        </Grid>
        {isPsaPrpCommentsOnInvoiceEnabled ? (
          <Grid item xs={12} sm={6}>
            <ShowCommentsOnInvoiceCheckBox
              showCommentsLabel={billingCardResourceKeys.showComments}
              showComments={showComments}
              isReadOnly={false}
              dataQeId="EditableShowCommentsOnInvoice"
            />
          </Grid>
        ) : null}
        <Divider className={classes.divider} />
        <Grid item xs={12} sm={12}>
          <FormSection
            classes={useMemo(
              () => ({
                legend: classes.legend
              }),
              [classes.legend]
            )}
            title={label(billingCardResourceKeys.structure)}
          />
        </Grid>
        <Grid item xs={12} sm={12}></Grid>
        <Grid item xs={12} sm={12}>
          <BillLineItemsByDropdown
            billLineItemOptions={billLineItemsBy}
            setFieldValue={setFieldValue}
          />
        </Grid>
        <Divider className={classes.divider} />
        <Grid item xs={12} sm={12}>
          <FormSection
            classes={useMemo(
              () => ({
                legend: classes.legend
              }),
              [classes.legend]
            )}
            title={label(billingCardResourceKeys.defaults)}
          />
        </Grid>
        <Grid item xs={12} sm={4}></Grid>
        <Box width="100%" />
        <Grid item xs={12} sm={4}>
          <TextField
            id="bill.description"
            variant="filled"
            fullWidth
            inputRef={inputRefCallback('description')}
            label={label(billingCardResourceKeys.description)}
            value={description ?? ''}
            error={hasError(errors, 'description')}
            helperText={getError(errors, 'description')}
            data-qe-id="Description"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            id="bill.internalNotes"
            variant="filled"
            multiline
            maxrows={isMobile ? 2 : 3}
            fullWidth
            inputRef={inputRefCallback('internalNotes')}
            label={label(billingCardResourceKeys.internalNotes)}
            value={internalNotes ?? ''}
            data-qe-id="InternalNotes"
          />
        </Grid>
      </Grid>
    </>
  );
};

BillingCardEditable.propTypes = {
  billingCardResourceKeys: PropTypes.any,
  isSubmitting: PropTypes.bool,
  errors: PropTypes.object.isRequired
};

export default BillingCardEditable;
